import { cn } from '@repo/lib/cn';
import { XIcon } from 'lucide-react';

import type { DirectoryProgress, FileProgress } from '@/hooks/file-upload';

import { UploadStateIcon } from '@/components/ui/featured-icon';
import {
  FileUploadList,
  FileUploadListAction,
  FileUploadListActions,
  FileUploadListDescription,
  FileUploadListDescriptionSeparator,
  FileUploadListDescriptionText,
  FileUploadListHeader,
  FileUploadListInfo,
  FileUploadListItem,
  FileUploadListName,
  FileUploadListSize,
} from '@/components/ui/file-upload';

interface FsItemsUploadListProps {
  files: FileProgress[];
  directories: DirectoryProgress[];
  abortDirectoryUpload?: (id: string) => void;
  abortFileUpload?: (id: string) => void;
}

export default function FsItemsUploadList(fsItemsUploadListProps: FsItemsUploadListProps) {
  const { files, directories, abortDirectoryUpload, abortFileUpload } = fsItemsUploadListProps;

  return (
    <FileUploadList className="flex h-full flex-col items-center justify-center gap-2">
      {directories.map((directory) => {
        const isPending: boolean = directory.status === 'uploading';
        const isError: boolean = directory.status === 'error';
        const isSuccess: boolean = directory.status === 'success';
        const isCanceled: boolean = directory.status === 'canceled';
        return (
          <div
            key={directory.id}
            data-visible={directory.status === 'uploading'}
            className={cn(
              'w-full overflow-hidden transition-[max-height,opacity] delay-3000 duration-400 ease-in-out',
              'data-[visible=false]:max-h-0 data-[visible=false]:opacity-0',
              'data-[visible=true]:max-h-40 data-[visible=true]:opacity-100'
            )}
          >
            <FileUploadListItem
              className={cn(
                'mx-auto flex w-full max-w-lg flex-row overflow-hidden transition-all duration-400 ease-in-out hover:max-w-xl',
                isPending && 'bg-surface-primary',
                isSuccess && 'bg-surface-success border-border-success',
                isError && 'bg-surface-error border-border-error',
                isCanceled && 'bg-surface-secondary/60 border-border-primary'
              )}
            >
              <FileUploadListHeader className="w-full">
                <UploadStateIcon isError={isError} isPending={isPending} isSuccess={isSuccess} isCanceled={isCanceled} />
                <FileUploadListInfo>
                  <FileUploadListName>{directory.name}</FileUploadListName>
                  <FileUploadListDescription>
                    <FileUploadListSize>{directory.filesCount}</FileUploadListSize>
                    <FileUploadListDescriptionSeparator />
                    <FileUploadListDescriptionText>{`${directory.uploadedFiles}/${directory.filesCount}`}</FileUploadListDescriptionText>
                  </FileUploadListDescription>
                </FileUploadListInfo>
                <FileUploadListActions className="ml-auto">
                  {/* Abort button */}
                  <FileUploadListAction className={cn(directory.status === 'uploading' ? 'visible' : 'hidden')} onClick={() => abortDirectoryUpload?.(directory.id)}>
                    <XIcon />
                    <span className="sr-only">Abort</span>
                  </FileUploadListAction>
                </FileUploadListActions>
              </FileUploadListHeader>
            </FileUploadListItem>
          </div>
        );
      })}
      {files.map((file) => {
        const isPending: boolean = file.status === 'uploading';
        const isError: boolean = file.status === 'error';
        const isSuccess: boolean = file.status === 'success';
        const isCanceled: boolean = file.status === 'aborted';
        return (
          <div
            key={file.id}
            data-visible={file.status === 'uploading'}
            className={cn(
              'w-full overflow-hidden transition-[max-height,opacity] delay-3000 duration-400 ease-in-out',
              'data-[visible=false]:max-h-0 data-[visible=false]:opacity-0',
              'data-[visible=true]:max-h-40 data-[visible=true]:opacity-100'
            )}
          >
            <FileUploadListItem
              className={cn(
                'mx-auto flex w-full max-w-lg flex-row overflow-hidden transition-all duration-400 ease-in-out hover:max-w-xl',
                isPending && 'bg-surface-primary',
                isSuccess && 'bg-surface-success border-border-success',
                isError && 'bg-surface-error border-border-error',
                isCanceled && 'bg-surface-secondary/60 border-border-primary'
              )}
            >
              <FileUploadListHeader className="w-full">
                <UploadStateIcon isError={isError} isPending={isPending} isSuccess={isSuccess} isCanceled={isCanceled} />
                <FileUploadListInfo>
                  <FileUploadListName>{file.file.name}</FileUploadListName>
                  <FileUploadListDescription>
                    <FileUploadListSize>{file.file.size}</FileUploadListSize>
                    <FileUploadListDescriptionSeparator />
                    <FileUploadListDescriptionText>{file.progress}%</FileUploadListDescriptionText>
                  </FileUploadListDescription>
                </FileUploadListInfo>
                <FileUploadListActions className="ml-auto">
                  {/* Abort button */}
                  <FileUploadListAction className={cn(file.status === 'uploading' ? 'visible' : 'hidden')} onClick={() => abortFileUpload?.(file.id)}>
                    <XIcon />
                    <span className="sr-only">Abort</span>
                  </FileUploadListAction>
                </FileUploadListActions>
              </FileUploadListHeader>
            </FileUploadListItem>
          </div>
        );
      })}
    </FileUploadList>
  );
}
